import { onBeforeUnmount, onMounted, watch } from "vue";
import { useRouter } from "vue-router";
import { useAuthStore } from "../store";
import { getItem } from "../core/utils/storage.helper";

const IDLE_TIMEOUT_IN_MS = 5 * 60 * 1000;
const ACTIVITY_EVENTS: Array<keyof WindowEventMap> = [
  "click",
  "keydown",
  "mousedown",
  "mousemove",
  "scroll",
  "touchstart",
];

export const useIdleLogout = () => {
  const router = useRouter();
  const authStore = useAuthStore();
  let inactivityTimeout: number | null = null;

  const hasActiveSession = () => {
    const token = getItem(import.meta.env.VITE_ACCESSTOKEN);
    return Boolean(token && token.rsa);
  };

  const isProtectedRoute = () => !router.currentRoute.value.meta?.public;

  const clearTimer = () => {
    if (inactivityTimeout !== null) {
      window.clearTimeout(inactivityTimeout);
      inactivityTimeout = null;
    }
  };

  const handleIdleLogout = async () => {
    if (!hasActiveSession() || !isProtectedRoute()) {
      clearTimer();
      return;
    }

    authStore.clearAuth();

    if (router.currentRoute.value.name !== "auth.login") {
      await router.replace({
        name: "auth.login",
        query: { reason: "timeout" },
      });
    }
  };

  const resetTimer = () => {
    clearTimer();

    if (!hasActiveSession() || !isProtectedRoute()) {
      return;
    }

    inactivityTimeout = window.setTimeout(
      handleIdleLogout,
      IDLE_TIMEOUT_IN_MS
    );
  };

  onMounted(() => {
    ACTIVITY_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, resetTimer, { passive: true });
    });

    document.addEventListener("visibilitychange", resetTimer);
    resetTimer();
  });

  watch(
    () => router.currentRoute.value.fullPath,
    () => {
      resetTimer();
    }
  );

  onBeforeUnmount(() => {
    clearTimer();
    ACTIVITY_EVENTS.forEach((eventName) => {
      window.removeEventListener(eventName, resetTimer);
    });
    document.removeEventListener("visibilitychange", resetTimer);
  });
};
